import Header1 from '@propertypal/shared/src/resources/images/pn-devs/crawfords-farm/image_1.jpg';
import Header2 from '@propertypal/shared/src/resources/images/pn-devs/crawfords-farm/image_2.jpg';
import Header3 from '@propertypal/shared/src/resources/images/pn-devs/crawfords-farm/image_3.jpg';
import Sitemap from '@propertypal/shared/src/resources/images/pn-devs/crawfords-farm/map.jpg';
import Property1 from '@propertypal/shared/src/resources/images/pn-devs/crawfords-farm/prop_1.jpg';
import Property2 from '@propertypal/shared/src/resources/images/pn-devs/crawfords-farm/prop_2.jpg';
import Property3 from '@propertypal/shared/src/resources/images/pn-devs/crawfords-farm/prop_3.jpg';
import Property4 from '@propertypal/shared/src/resources/images/pn-devs/crawfords-farm/prop_4.jpg';
import Title1 from '@propertypal/shared/src/resources/images/pn-devs/crawfords-farm/title_1.png';
import Title2 from '@propertypal/shared/src/resources/images/pn-devs/crawfords-farm/title_2.jpg';
import Title3 from '@propertypal/shared/src/resources/images/pn-devs/crawfords-farm/title_3.jpg';
import React from 'react';
import { useTheme } from 'styled-components';
import { Text, TextProps } from '../../typography';
import CrawfordsFarmDetails, { CrawfordsFarmDetailsProps } from './CrawfordsFarmDetails.pn';
import CrawfordsFarmHeader from './CrawfordsFarmHeader.pn';
import CrawfordsFarmSummary, { CrawfordsFarmSummaryProps } from './CrawfordsFarmSummary.pn';
import DevelopmentContent, { DevelopmentSection } from './DevelopmentContent.pn';
import { DevHeaderImg, DevImgGrid, DevTitleImg } from './DevelopmentContent.style.pn';

interface CrawfordsFarmDevProps extends Omit<CrawfordsFarmSummaryProps, 'children'>, CrawfordsFarmDetailsProps {
  descriptionBox: React.ReactNode;
  propertyTypes: React.ReactNode;
}

const textProps: TextProps = {
  fontSize: 21,
  lineHeight: 25,
  mt: 8,
  mb: 16,
};

const CrawfordsFarmDev: React.FC<CrawfordsFarmDevProps> = ({
  sliderProps,
  slideshowLinksProps,
  descriptionBox,
  locationDetails,
  contactDetails,
  propertyTypes,
}) => {
  const theme = useTheme();
  const bg = theme.background;
  return (
    <div data-testid="crawfords-farm-development">
      <CrawfordsFarmHeader />
      <CrawfordsFarmSummary slideshowLinksProps={slideshowLinksProps} sliderProps={sliderProps}>
        {descriptionBox}
      </CrawfordsFarmSummary>
      <CrawfordsFarmDetails locationDetails={locationDetails} contactDetails={contactDetails} />
      {propertyTypes}
      <DevelopmentContent bg={bg} fillBg="#d9d9d9">
        <DevelopmentSection bg={bg} narrow>
          <DevHeaderImg src={Header1.src} alt="Crawfords Farm Developments" />
          <DevTitleImg src={Title1.src} width="281" alt="Coastal &amp; Country Title" />
          <Text fontWeight="bold" {...textProps}>
            The McAlorum Group is delighted to introduce the latest offering to their near 60-year history of bringing
            quality development to this most sought after Bangor West location.
          </Text>
          <Text {...textProps}>
            Crawfords Farm sits adjacent to the Crawfordsburn Road, bordering the countryside between Bangor and
            Crawfordsburn village. Pedestrian access to the Crawfordsburn Road brings easy access to both the shoreline
            and the village, without the need for a car. Carnalea train station is within walking distance for the
            easiest commute to both Bangor and Belfast. Crawfords Farm will provide traditionally built brick and block
            constructed homes designed to reduce running costs and environmental impact. A designled brief provides open
            spaces and carefully planned road layouts to complement this alluring countryside setting. Crawfords Farm
            caters for first time buyers, growing families and those looking to enhance the quality of life in their
            later years. We anticipate high demand from all sections of the market given the variety of house styles on
            offer and the lifestyle opportunities within this coastal and country environment.
          </Text>
        </DevelopmentSection>
        <DevelopmentSection bg={bg} narrow>
          <DevHeaderImg src={Header2.src} alt="Crawfords Farm Development Interior" mb={16} />
          <DevTitleImg src={Title2.src} alt="Luxurious Living" maxW={281} />
          <Text fontWeight="bold" {...textProps}>
            Crawfords Farm presents classically designed homes with contemporary twists inspired by the rich heritage of
            this gentrified coastal landscape.
          </Text>
          <Text {...textProps}>
            The architectural style blends seamlessly into the mature avenues and residences that make Bangor West such
            an aspirational lifetime address. Each beautifully crafted home is defined by stunning architecture
            showcased by a mix of traditional brick and render finishes with subtle pastel fascias reminiscent of the
            original Victorian villas that frame North Down&apos;s seafront. Moving inside, and every layout offers the
            highest standard of comfortable living with an emphasis on space and light throughout.
          </Text>
          <DevImgGrid>
            <img src={Property1.src} alt="The Carnalea, Crawfords Farm, Bangor" />
            <img src={Property2.src} alt="The Crawford, Crawfords Farm, Bangor" />
            <img src={Property3.src} alt="The Rosevilee, Crawfords Farm, Bangor" />
            <img src={Property4.src} alt="The Sharman, Crawfords Farm, Bangor" />
          </DevImgGrid>
        </DevelopmentSection>
        <DevelopmentSection bg={bg} narrow>
          <DevHeaderImg src={Header3.src} alt="Crawfords Farm Location" mb={16} />
          <DevTitleImg src={Title3.src} alt="An Ideal Location" />
          <Text fontWeight="bold" {...textProps}>
            Located on the southern shore of Belfast Lough as it opens onto the Irish Sea, Bangor is a charming seaside
            town that has kept much of its Victorian charm.
          </Text>
          <Text {...textProps}>
            The iconic marina at its centre epitomises a nautical and coastal culture that makes Bangor one of the
            province&apos;s most desirable places to live. Undoubtedly, part of the appeal is its proximity to Belfast,
            located just 13 miles to the west and well connected by road and rail. However, the town is much more than a
            commuter belt. Bangor has its own unique identity, and at a time when we feel more connected than ever to
            our surroundings, residents will enjoy a vibrant town, peaceful surrounding North Down countryside and the
            unspoiled shoreline in equal measure. T here are few areas that offer so many activities and attractions in
            such close proximity. Country parks, beaches, coastal trails, marine gardens, cycle paths and golf courses
            are accessible to everyone and they define the outdoor lifestyle of the area. Bangor also has the amenities
            and essentials you expect from an established and selfcontained town. From an abundance of vibrant
            restaurants, cafes, bakeries, butchers and greengrocers, to a great range of independent boutiques,
            supermarkets, schools and leisure facilities. The area delivers a perfect balance of convenience and
            comfort.
          </Text>
          <DevHeaderImg src={Sitemap.src} alt="Crawfords Farm Sitemap" mb={16} />
        </DevelopmentSection>
      </DevelopmentContent>
    </div>
  );
};

export default CrawfordsFarmDev;
